/**
 * useNovidadesDoFeed — o número de posts novos do Feed no menu lateral (Task 4 do plano "Feed:
 * número de posts novos no menu", spec §5.4 e §7).
 *
 * Mora no Sidebar, que existe em toda página: o número tem de estar vivo com o Feed fechado.
 *
 *   - O servidor conta (`GET /feed/novidades`, só o que a pessoa ainda não viu e pode ver).
 *   - Fora da página do Feed o hook abre UM socket do feed (`/api/v1/ws/feed`, o mesmo do
 *     `useFeedTimeline`). O servidor só entrega cada `post:new` a quem pode ver o post.
 *       · `post:new` de outra pessoa SOMA no navegador (`aplicarPostNovo`), sem novo `GET`: com o
 *         `GET` a cada post, cada post público faria ~144 pessoas buscarem ao mesmo tempo.
 *       · `post:deleted` e a abertura do socket REFAZEM a busca (o servidor sabe se o post apagado
 *         contava; a abertura cobre o que chegou enquanto o socket estava fora). Com o número em 0
 *         e sem busca em voo o `post:deleted` não refaz nada: apagar um post não reduz zero.
 *       · Rede de segurança: a busca também refaz ao voltar o foco para a janela (fora da página do
 *         Feed: lá o foco marca "visto" e o número é zero).
 *   - Na página do Feed já existe o socket da timeline e o número é zero com a aba à vista: este
 *     hook fecha o dele (não há dois sockets do feed na mesma aba).
 *
 * Com o socket aberto o hook manda `{"type":"ping"}` a cada 30 s (keepalive contra o proxy que corta
 * o ocioso); o `pong` que volta é ignorado.
 *
 * A reconexão espera mais a cada queda, com jitter (`atrasoDeReconexao`), para que um restart do
 * backend não vire uma enxurrada de apertos de mão e de `GET`s. Depois de `MAX_TENTATIVAS` a espera
 * passa a ser a lenta (uma tentativa por minuto, para sempre): um deploy de vários minutos não deixa
 * o número do menu parado até recarregar a página. Ficar `online` ou a aba voltar a ficar visível
 * rearma o contador e tenta na hora.
 */
import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getWsUrl } from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { abaVisivel, assinarAbaVisivel } from "@/lib/avisos/abaVisivel";
import feedService, { type NovidadesDoFeed } from "@/services/feedService";
import type { FeedWsEvent } from "@/types/feed";
import { FEED_WS_PATH } from "./useFeedTimeline";

export const NOVIDADES_DO_FEED_KEY = ["feed", "novidades"] as const;

/**
 * Depois de tantas quedas seguidas a espera passa a ser a LENTA (`ESPERA_LENTA_MS`): o socket não
 * desiste (`online` / aba visível rearmam na hora, sem esperar).
 */
const MAX_TENTATIVAS = 8;
/**
 * A espera de depois do teto: uma tentativa por minuto (mais o jitter), para sempre. Um deploy do
 * SE_WEB leva vários minutos, e quem fica na mesma aba (ex.: `/mensagens` o dia todo) não pode ver
 * o número parado até recarregar a página; um laço apertado, por outro lado, derrubaria o backend
 * que acabou de voltar.
 */
const ESPERA_LENTA_MS = 60_000;
/** A espera base dobra a cada queda até aqui; o jitter soma até 1 s por cima (máximo 31 s). */
const TETO_DA_ESPERA_MS = 30_000;
const JITTER_MS = 1_000;
/**
 * Só uma conexão que ficou aberta por este tempo zera o contador de quedas. Uma que abre e o
 * servidor derruba em seguida (ex.: erro depois do aceite) não pode virar um laço de 1 s.
 */
const ESTAVEL_APOS_MS = 10_000;
/**
 * Ping de keepalive com o socket aberto (o servidor responde `pong`, que este hook ignora). O socket
 * do menu fica ocioso por horas e o proxy da produção (Traefik, `readTimeout` de 60 s) cortaria o
 * ocioso: cada pessoa reconectaria e buscaria o número, em laço. Mesmo molde do `useChat`.
 */
const PING_MS = 30_000;

export interface EstadoDasNovidades {
  total: number;
  /** Ids dos posts já somados aqui desde a última resposta do servidor. */
  contados: ReadonlySet<string>;
}

/**
 * Pura: a conta local de um `post:new`. Soma 1 só se o autor não é a própria pessoa e o post
 * ainda não foi contado (o mesmo evento pode chegar por dois canais). Sem mudança, devolve o
 * MESMO `estado`.
 */
export function aplicarPostNovo(
  estado: EstadoDasNovidades,
  post: { id: string; author_id: string },
  meuId: string
): EstadoDasNovidades {
  if (post.author_id === meuId || estado.contados.has(post.id)) return estado;
  return { total: estado.total + 1, contados: new Set([...estado.contados, post.id]) };
}

/**
 * A espera (ms) antes da tentativa `tentativa` (0 = a 1ª reconexão): 1 s, 2 s, 4 s... até 30 s,
 * mais um jitter de 0 a 1 s. Não decresce de uma tentativa para a seguinte e fica em no máximo
 * 31 s. `aleatorio` existe para o teste.
 */
export function atrasoDeReconexao(
  tentativa: number,
  aleatorio: () => number = Math.random
): number {
  return Math.min(TETO_DA_ESPERA_MS, 1_000 * 2 ** tentativa) + aleatorio() * JITTER_MS;
}

interface Opcoes {
  /** A pessoa pode ler o feed (`feed:read`). Sem isso: zero, sem busca e sem socket. */
  ativo: boolean;
  /** Id de quem está logado (o próprio post não conta). */
  meuId: string | null;
  /** O caminho atual é a página do Feed. */
  naPaginaDoFeed: boolean;
}

/** Chame UMA vez, no Sidebar. */
export function useNovidadesDoFeed({ ativo, meuId, naPaginaDoFeed }: Opcoes): { total: number } {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  // Lidos por ref: o AuthProvider cria um `getToken` novo a cada redesenho e o `meuId` pode chegar
  // depois do primeiro render. Nenhum dos dois pode derrubar e reabrir o socket.
  const getTokenRef = useRef(getToken);
  const meuIdRef = useRef(meuId);
  useEffect(() => {
    getTokenRef.current = getToken;
    meuIdRef.current = meuId;
  }, [getToken, meuId]);

  // Posts já somados aqui. Zera quando a resposta do servidor chega (ela já os inclui).
  const contadosRef = useRef<ReadonlySet<string>>(new Set());

  const { data } = useQuery({
    queryKey: NOVIDADES_DO_FEED_KEY,
    queryFn: async (): Promise<NovidadesDoFeed> => {
      const resposta = await feedService.getNovidades();
      contadosRef.current = new Set();
      return resposta;
    },
    enabled: ativo,
    // O padrão do app é `false`; fora do Feed o foco é a rede de segurança de quem perdeu evento.
    // Na página do Feed o foco já dispara o POST "visto" (`useMarcarFeedComoVisto`): um GET junto com
    // ele lê o banco antes do commit e devolve o total velho por cima do zero.
    refetchOnWindowFocus: !naPaginaDoFeed,
  });

  // `boolean`: comparável por valor, o React não re-renderiza sem mudança.
  const aVista = useSyncExternalStore(assinarAbaVisivel, abaVisivel, () => true);

  const socketAberto = ativo && !naPaginaDoFeed;
  useEffect(() => {
    if (!socketAberto) return;

    let ws: WebSocket | null = null;
    let espera: ReturnType<typeof setTimeout> | null = null;
    let estavel: ReturnType<typeof setTimeout> | null = null;
    let ping: ReturnType<typeof setInterval> | null = null;
    let tentativas = 0;

    const pararOPing = () => {
      if (ping) clearInterval(ping);
      ping = null;
    };

    const refazerABusca = () => {
      void queryClient.invalidateQueries({ queryKey: NOVIDADES_DO_FEED_KEY });
    };

    const aoPostNovo = (post: { id: string; author_id: string }) => {
      const meu = meuIdRef.current;
      const atual = queryClient.getQueryData<NovidadesDoFeed>(NOVIDADES_DO_FEED_KEY);
      // Sem o id da pessoa não dá para descontar o próprio post: quem sabe é o servidor.
      if (!meu) return refazerABusca();

      const antes: EstadoDasNovidades = { total: atual?.total ?? 0, contados: contadosRef.current };
      const depois = aplicarPostNovo(antes, post, meu);
      if (depois === antes) return; // post dela mesma, ou já contado

      // Busca em voo (ou ainda sem número): a resposta pode ser anterior a este post e apagaria a
      // soma. Refaz a busca, que já o inclui (o evento sai depois do commit).
      if (!atual || queryClient.isFetching({ queryKey: NOVIDADES_DO_FEED_KEY }) > 0) {
        return refazerABusca();
      }
      contadosRef.current = depois.contados;
      queryClient.setQueryData<NovidadesDoFeed>(NOVIDADES_DO_FEED_KEY, { total: depois.total });
    };

    const aoPostApagado = () => {
      // Apagar um post não reduz zero: sem número e sem busca em voo, não há o que corrigir. Sem esta
      // saída, cada post público apagado faria ~144 pessoas buscarem ao mesmo tempo (spec §5.4).
      const atual = queryClient.getQueryData<NovidadesDoFeed>(NOVIDADES_DO_FEED_KEY);
      const buscando = queryClient.isFetching({ queryKey: NOVIDADES_DO_FEED_KEY }) > 0;
      if (atual?.total === 0 && !buscando) return;
      refazerABusca();
    };

    const aoEvento = (evento: FeedWsEvent) => {
      if (evento.type === "post:new") aoPostNovo(evento.post);
      else if (evento.type === "post:deleted") aoPostApagado();
    };

    const agendar = () => {
      if (tentativas >= MAX_TENTATIVAS) {
        // Passou do teto: não desiste, tenta devagar. O contador não sobe mais; só uma conexão
        // estável (`ESTAVEL_APOS_MS`) ou `online` / aba visível o zeram.
        espera = setTimeout(conectar, ESPERA_LENTA_MS + Math.random() * JITTER_MS);
        return;
      }
      espera = setTimeout(conectar, atrasoDeReconexao(tentativas));
      tentativas += 1;
    };

    function conectar() {
      espera = null;
      pararOPing();
      const token = getTokenRef.current();
      if (!token) return;

      let nova: WebSocket;
      try {
        nova = new WebSocket(`${getWsUrl(FEED_WS_PATH)}?token=${encodeURIComponent(token)}`);
      } catch (err) {
        console.error("[NovidadesDoFeed] não foi possível criar o WebSocket:", err);
        return;
      }
      ws = nova;

      // Conexão já largada (cleanup, nova conexão no lugar) não mexe em nada.
      nova.onopen = () => {
        if (ws !== nova) return;
        refazerABusca();
        estavel = setTimeout(() => {
          tentativas = 0;
        }, ESTAVEL_APOS_MS);
        pararOPing();
        ping = setInterval(() => {
          if (nova.readyState === WebSocket.OPEN) nova.send(JSON.stringify({ type: "ping" }));
        }, PING_MS);
      };

      nova.onmessage = (bruto) => {
        if (ws !== nova) return;
        try {
          const dados = JSON.parse(bruto.data);
          if (dados && typeof dados === "object" && typeof dados.type === "string") {
            aoEvento(dados as FeedWsEvent);
          }
        } catch (err) {
          console.warn("[NovidadesDoFeed] mensagem inválida:", err);
        }
      };

      // O `onclose` vem em seguida e cuida da reconexão.
      nova.onerror = () => {};

      nova.onclose = () => {
        if (ws !== nova) return;
        ws = null;
        if (estavel) clearTimeout(estavel);
        estavel = null;
        pararOPing();
        agendar();
      };
    }

    /** `online` ou aba visível: se o socket não está de pé, zera as quedas e tenta já. */
    const rearmar = () => {
      if (ws) return;
      if (espera) clearTimeout(espera);
      espera = null;
      tentativas = 0;
      conectar();
    };
    const aoMudarAVisibilidade = () => {
      if (abaVisivel()) rearmar();
    };

    window.addEventListener("online", rearmar);
    document.addEventListener("visibilitychange", aoMudarAVisibilidade);
    conectar();

    return () => {
      window.removeEventListener("online", rearmar);
      document.removeEventListener("visibilitychange", aoMudarAVisibilidade);
      if (espera) clearTimeout(espera);
      if (estavel) clearTimeout(estavel);
      pararOPing();
      // `ws` esvazia ANTES do `close()`: o `onclose` (que chega depois) não agenda reconexão.
      const atual = ws;
      ws = null;
      atual?.close();
    };
  }, [socketAberto, queryClient]);

  const total = !ativo || (naPaginaDoFeed && aVista) ? 0 : (data?.total ?? 0);
  return useMemo(() => ({ total }), [total]);
}
